import register from 'preact-custom-element'
import { fetchProductByCollectionHandle } from 'libs/shopify-product'
import { useState, useEffect } from 'preact/hooks'
import { normalizeProducts } from '@/components/libs/shopify-normalize'
import ProductCarouselSimple from 'modules/product-carousel-simple/product-carousel-simple'
import { SkeletonProduct } from 'snippets/skeleton/skeleton'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import { fetchProductsLinked } from 'helpers/fetch'
import './searchspring-popular-product.css'

function NostoPopularProduct(props) {
  const [settings] = useState(() => {
    // Safely get settings, may be undefined if script hasn't loaded yet
    return window[`popularProductConfig${props.index}`] || {}
  })
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const getSearchspringRecommendedProducts = async () => {
    try {
      if (!settings || !settings.searchspringEnabled || !settings.searchspringSiteId) {
        console.warn('SearchSpring not configured, falling back to collection products')
        const recommendations = await getRelatedProducts(settings?.productsCollection)
        if (recommendations && recommendations.length > 0) {
          return normalizeProducts(recommendations, 'shopify-graphql')
        }
        return []
      }

      if (settings.searchspringEnabled && settings.searchspringSiteId) {
        let productIds = []
        if (settings.searchResults) {
          productIds = settings.searchResults.map(item => item.id)
        }
        if (settings.productId) {
          productIds.push(settings.productId)
        }
        const options = {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            profiles: [{ tag: settings.searchspringTag }],
            categories: [settings.productsCollection],
            products: productIds
          })
        }

        const response = await fetch(
          `https://${settings.searchspringSiteId}.a.searchspring.io/boost/${settings.searchspringSiteId}/recommend`,
          options
        )

        const data = await response.json()
        const dataObject = data[0]

        if (dataObject?.results?.length > 0) {
          const result = normalizeProducts(dataObject.results, 'searchspring-recommended')
          return result
        }
      }

      // fallback to Shopify GraphQL related products
      const recommendations = await getRelatedProducts(settings.productsCollection)
      if (recommendations && recommendations.length > 0) {
        return normalizeProducts(recommendations, 'shopify-graphql')
      }

      return []
    } catch (err) {
      console.error('Searchspring fetch error:', err)
      return []
    }
  }

  const getRelatedProducts = async (collection) => {
    if (collection === null) return null
    const filters = [
      {
        available: true
      }
    ]
    return await fetchProductByCollectionHandle(collection, filters, 8)
  }

  useEffect(() => {
    let isMounted = true

    const loadProducts = async () => {
      try {
        setIsLoading(true)
        const response = await getSearchspringRecommendedProducts()

        if (!isMounted) return

        if (response && response.length > 0) {
          // Debug: Check the normalized product data
          console.log('[SearchSpring Popular] Normalized products:', response)
          console.log('[SearchSpring Popular] First product:', response[0])

          // Fetch linked products (colors, variants) with error handling
          try {
            await fetchProductsLinked(response)
          } catch (linkError) {
            console.warn('Failed to fetch linked products, continuing with basic data:', linkError)
          }

          if (isMounted) {
            setProducts(response)
          }
        } else {
          console.warn('[SearchSpring Popular] No products returned from API')
          if (isMounted) {
            setProducts([])
          }
        }
      } catch (error) {
        console.error('Error loading SearchSpring products:', error)
        if (isMounted) {
          setProducts([])
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    // Delay initialization to ensure config is loaded
    const timeoutId = setTimeout(loadProducts, 100)

    return () => {
      isMounted = false
      clearTimeout(timeoutId)
    }
  }, [])

  return (
    <div className="container relative overflow-hidden">
      <div className="carousel-heading flex flex-col gap-1 max-w-[310px] sm:max-w-[496px] md:gap-2">
        {props.heading && <div className="title text-2xl md:text-3xl font-heading">
          {props.heading}
        </div>}
        {props.sub_heading && <div className="text-sm" dangerouslySetInnerHTML={{ __html: props.sub_heading }}></div>}
      </div>
      <div className="w-full">
        {isLoading ? (
          <div className="relative">
            <div className="hidden grid-cols-1 gap-4 2xl:grid 2xl:grid-cols-5">{SkeletonProduct(5)}</div>
            <div className="grid grid-cols-1 gap-4 lg-max:hidden lg:grid-cols-4 2xl:hidden">{SkeletonProduct(4)}</div>
            <div className="h-72 lg:hidden"><LoaderSpin width="8" height="8" /></div>
          </div>
        ) : (
          products && products.length > 0 ? (
            <ProductCarouselSimple products={products} scrollbar={true} navigation={true} centerSlides={true} className="product-carousel-popular" />
          ) : (
            <div className="text-lg font-medium">No product</div>
          )
        )}
      </div>
    </div>
  )
}

if (!customElements.get('searchspring-popular-product')) {
  register(NostoPopularProduct, 'searchspring-popular-product', ['heading', 'subHeading', 'index'])
}
